"use client";

import { cloneElement, isValidElement } from "react";
import { trackButtonClick } from "@/utils/analytics";

interface TrackedButtonWrapperProps {
  buttonId?: string;
  buttonText: string;
  pageSection: string;
  destination: string;
  children: React.ReactElement;
}

/**
 * Client component wrapper that adds click tracking to server-rendered buttons/links
 * Wraps a Link or Button component and adds analytics tracking on click
 */
export default function TrackedButtonWrapper({
  buttonId,
  buttonText,
  pageSection,
  destination,
  children,
}: TrackedButtonWrapperProps) {
  // Clone the child element and add/merge onClick handler
  if (!isValidElement(children)) {
    return children;
  }

  // Get existing onClick handler if it exists
  const existingOnClick = (
    children.props as { onClick?: (e: React.MouseEvent) => void }
  )?.onClick;

  const handleClick = (e: React.MouseEvent) => {
    // Track the button click
    trackButtonClick({
      button_id: buttonId,
      button_text: buttonText,
      page_section: pageSection,
      destination,
    });

    // Call original onClick if it exists (merge handlers)
    if (existingOnClick) {
      existingOnClick(e);
    }
  };

  // Merge onClick handlers if one already exists
  const mergedOnClick = existingOnClick
    ? (e: React.MouseEvent) => {
        handleClick(e);
        existingOnClick(e);
      }
    : handleClick;

  return cloneElement(children, {
    onClick: mergedOnClick,
  } as React.HTMLAttributes<HTMLElement>);
}
